/**
 * RadarPreview Component
 * Renders a radar / spider chart using raw SVG — polygons drawn from polar coordinates.
 * Supports multiple series as overlapping filled polygons.
 */

import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useData } from '../../contexts/DataContext';
import transformData from '../../utils/transformData';
import { getColors } from '../../charts/shared/palettes';
import { useChartTooltip } from './useChartTooltip';
import ChartTitle from './ChartTitle';
import { computeChartLayout, computeTitleFontSize, LEGEND_SPACING } from '../../utils/chartLayout';
import { measureTextWidth } from '../../utils/textMeasure';

/**
 * Convert polar (angle, t) to cartesian (x, y) relative to (cx, cy).
 * angle is in radians, starting from top (−π/2), going clockwise.
 * t ∈ [0, 1] where 1 = full radius.
 */
function polar(cx, cy, radius, angle, t) {
  return {
    x: cx + t * radius * Math.cos(angle),
    y: cy + t * radius * Math.sin(angle),
  };
}

/**
 * Compute SVG text-anchor and dominant-baseline based on angle.
 */
function labelAlignment(angle) {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const textAnchor = cos > 0.15 ? 'start' : cos < -0.15 ? 'end' : 'middle';
  const dominantBaseline = sin > 0.15 ? 'hanging' : sin < -0.15 ? 'auto' : 'middle';
  return { textAnchor, dominantBaseline };
}

/* ── Label bounding-box helpers ──────────────────────────────────────────────── */

function labelBBox(x, y, w, h, textAnchor, dominantBaseline) {
  const left = textAnchor === 'start' ? x : textAnchor === 'end' ? x - w : x - w / 2;
  const top = dominantBaseline === 'hanging' ? y : dominantBaseline === 'auto' ? y - h : y - h / 2;
  return { left, top, right: left + w, bottom: top + h };
}

function bboxesOverlap(a, b, pad = 2) {
  return !(a.right + pad < b.left || b.right + pad < a.left ||
           a.bottom + pad < b.top || b.bottom + pad < a.top);
}

/* ── Axis-label anti-overlap resolver ────────────────────────────────────────── *
 *
 * Three-phase strategy applied in order until no overlaps remain:
 *   1. Push overlapping labels radially outward (bounded at MAX_DIST).
 *   2. Reduce font size globally (down to minFontSize).
 *   3. Truncate the longer label in each remaining overlapping pair.
 *
 * Deterministic, input-agnostic, and bounded so labels stay within the SVG.
 */
function resolveRadarLabelPositions({ labels, cx, cy, radius, N, fontSize, fontFamily, minFontSize = 9 }) {
  if (N === 0 || radius <= 0) return { positions: [], fontSize };

  const angleFor = (i) => -Math.PI / 2 + (i / N) * 2 * Math.PI;
  const BASE_DIST = 1.15;
  const MAX_DIST = 1.50;
  const STEP = 0.03;
  const MAX_ITERS = 20;
  const PAD = 4;

  function build(fs, dists) {
    return labels.map((lbl, i) => {
      const angle = angleFor(i);
      const d = dists[i];
      const x = cx + d * radius * Math.cos(angle);
      const y = cy + d * radius * Math.sin(angle);
      const { textAnchor, dominantBaseline } = labelAlignment(angle);
      const w = measureTextWidth(String(lbl), { fontSize: fs, fontFamily });
      return {
        label: String(lbl), displayLabel: String(lbl),
        x, y, angle, distance: d,
        textAnchor, dominantBaseline,
        width: w, height: fs * 1.3,
      };
    });
  }

  function bbox(p) {
    return labelBBox(p.x, p.y, p.width, p.height, p.textAnchor, p.dominantBaseline);
  }

  function overlapPairs(pos) {
    const pairs = [];
    for (let i = 0; i < pos.length; i++)
      for (let j = i + 1; j < pos.length; j++)
        if (bboxesOverlap(bbox(pos[i]), bbox(pos[j]), PAD)) pairs.push([i, j]);
    return pairs;
  }

  function pushOut(fs) {
    const dists = labels.map(() => BASE_DIST);
    let pos = build(fs, dists);
    if (N <= 1) return pos;
    for (let it = 0; it < MAX_ITERS; it++) {
      const pairs = overlapPairs(pos);
      if (pairs.length === 0) return pos;
      let pushed = false;
      for (const [i, j] of pairs) {
        if (dists[i] < MAX_DIST) { dists[i] = Math.min(MAX_DIST, dists[i] + STEP); pushed = true; }
        if (dists[j] < MAX_DIST) { dists[j] = Math.min(MAX_DIST, dists[j] + STEP); pushed = true; }
      }
      if (!pushed) break;
      pos = build(fs, dists);
    }
    return pos;
  }

  // Phase 1: push at current font size
  let pos = pushOut(fontSize);
  let fs = fontSize;
  if (overlapPairs(pos).length === 0) return { positions: pos, fontSize: fs };

  // Phase 2: shrink font
  for (fs = fontSize - 1; fs >= minFontSize; fs--) {
    pos = pushOut(fs);
    if (overlapPairs(pos).length === 0) return { positions: pos, fontSize: fs };
  }
  fs = minFontSize;

  // Phase 3: truncate remaining overlapping labels
  const remaining = overlapPairs(pos);
  for (const [i, j] of remaining) {
    if (!bboxesOverlap(bbox(pos[i]), bbox(pos[j]), PAD)) continue;
    const longer = pos[i].displayLabel.length >= pos[j].displayLabel.length ? i : j;
    let len = pos[longer].displayLabel.length;
    while (len > 3) {
      len--;
      pos[longer].displayLabel = pos[longer].label.slice(0, len) + '\u2026';
      pos[longer].width = measureTextWidth(pos[longer].displayLabel, { fontSize: fs, fontFamily });
      if (!bboxesOverlap(bbox(pos[i]), bbox(pos[j]), PAD)) break;
    }
  }

  return { positions: pos, fontSize: fs };
}

/* ── Wrapping legend layout (measured text widths) ───────────────────────────── *
 *
 * Flows legend items left-to-right, wrapping to a new centered row when the
 * next item would exceed the available width.  Truncates individual items that
 * are wider than the entire available width.
 */
function computeWrappingLegendLayout(names, availableWidth, fontSize, fontFamily) {
  const SWATCH = LEGEND_SPACING.rectSize;
  const SWATCH_GAP = 4;
  const ITEM_GAP = LEGEND_SPACING.itemGap;
  const ROW_H = LEGEND_SPACING.rowHeight;

  const items = names.map((name, idx) => {
    const tw = measureTextWidth(String(name), { fontSize, fontFamily });
    return { idx, name: String(name), textWidth: tw, totalWidth: SWATCH + SWATCH_GAP + tw };
  });

  for (const item of items) {
    if (item.totalWidth > availableWidth && item.name.length > 4) {
      let trunc = item.name;
      while (trunc.length > 3) {
        trunc = trunc.slice(0, -1);
        const tw = measureTextWidth(trunc + '\u2026', { fontSize, fontFamily });
        if (SWATCH + SWATCH_GAP + tw <= availableWidth) {
          item.name = trunc + '\u2026';
          item.textWidth = tw;
          item.totalWidth = SWATCH + SWATCH_GAP + tw;
          break;
        }
      }
    }
  }

  const rows = [];
  let row = [];
  let rowW = 0;

  for (const item of items) {
    const needed = row.length > 0 ? ITEM_GAP + item.totalWidth : item.totalWidth;
    if (row.length > 0 && rowW + needed > availableWidth) {
      rows.push({ items: [...row], width: rowW });
      row = [];
      rowW = 0;
    }
    rowW += row.length > 0 ? ITEM_GAP + item.totalWidth : item.totalWidth;
    row.push(item);
  }
  if (row.length > 0) rows.push({ items: [...row], width: rowW });

  return { rows, rowCount: rows.length, rowHeight: ROW_H, totalHeight: Math.max(0, rows.length * ROW_H) };
}

/* ── Component ───────────────────────────────────────────────────────────────── */

function RadarPreview({ chartType, config, width, height }) {
  const { resolved: theme } = useTheme();
  const { isLoaded, derivedData: userData, mapping } = useData();

  const data = useMemo(() => {
    if (isLoaded && userData.length > 0 && Object.keys(mapping).length > 0) {
      return transformData(userData, mapping, chartType.id);
    }
    if (chartType.getSampleData) return chartType.getSampleData();
    return chartType.sampleData;
  }, [isLoaded, userData, mapping, chartType]);

  const axes = useMemo(() => {
    if (!data.length || !data[0].values) return [];
    return Object.keys(data[0].values);
  }, [data]);

  const N = axes.length;

  const maxPerAxis = useMemo(() => {
    const maxes = {};
    axes.forEach(axis => {
      maxes[axis] = Math.max(...data.map(d => d.values[axis] || 0), 1);
    });
    return maxes;
  }, [data, axes]);

  const colors = useMemo(() => getColors(config, Math.max(data.length, 1), theme), [config, data.length, theme]);

  // Styling — defined early so legend/label layout can reference font metrics
  const tickFont = {
    fill: theme.tickColor || theme.foreground || '#000',
    fontSize: theme.tickSize ?? 13,
    fontFamily: theme.fontBody || theme.fontHeading || 'Cohere Text',
  };

  const legendFont = {
    fill: theme.foreground || '#000',
    fontSize: theme.legendSize ?? 12,
    fontFamily: theme.fontBody || theme.fontHeading || 'Cohere Text',
  };

  // ─── Legend layout (wrapping rows with measured text) ────────────────────────
  const legendLayout = useMemo(() => {
    if (!config.showLegend || data.length <= 1) {
      return { rows: [], rowCount: 0, rowHeight: 0, totalHeight: 0 };
    }
    return computeWrappingLegendLayout(
      data.map(s => s.name),
      width - 40,
      legendFont.fontSize,
      legendFont.fontFamily,
    );
  }, [config.showLegend, data, width, legendFont.fontSize, legendFont.fontFamily]);

  // Layout: title and overall margins
  const radarLayout = computeChartLayout({
    width,
    height,
    title: config.title,
    titleFontSize: computeTitleFontSize(config.title, width) || undefined,
    tickFontSize: theme.tickSize ?? 13,
    tickFontFamily: theme.fontBody,
    hasXLabel: false,
    hasYLabel: false,
    orientation: 'vertical',
    baseRight: 20,
    baseBottom: 20,
    legend: {
      show: !!(config.showLegend && data.length > 1),
      position: 'bottom',
      bottomSpace: Math.max(40, legendLayout.totalHeight + 16),
      topSpace: 0,
      rightSpace: 0,
    },
  });

  const titleHeight = radarLayout.margin.top;
  const legendHeight = legendLayout.totalHeight > 0 ? legendLayout.totalHeight + 16 : 0;

  // Dynamic label margin based on longest axis label
  const labelMargin = useMemo(() => {
    if (N === 0) return 32;
    const maxPx = Math.max(...axes.map(a =>
      measureTextWidth(String(a), { fontSize: tickFont.fontSize, fontFamily: tickFont.fontFamily })
    ));
    return Math.max(32, Math.min(90, Math.ceil(maxPx * 0.5 + 18)));
  }, [axes, N, tickFont.fontSize, tickFont.fontFamily]);

  // Radar center and radius
  const cx = width / 2;
  const availableH = height - titleHeight - legendHeight - labelMargin * 2;
  const availableW = width - labelMargin * 2;
  const radius = Math.max(20, Math.min(availableW, availableH) / 2);
  const cy = titleHeight + labelMargin + radius;

  const angleFor = (i) => -Math.PI / 2 + (i / N) * 2 * Math.PI;

  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const gridColor = theme.gridColor || '#CCCCCC';
  const gridAlpha = theme.gridAlpha ?? 0.4;
  const fillAlpha = config.fillAlpha ?? 0.2;

  // ─── Resolved axis label positions (collision-free) ──────────────────────────
  const resolvedLabels = useMemo(() => {
    if (!config.showAxisLabels || N === 0) return { positions: [], fontSize: tickFont.fontSize };
    return resolveRadarLabelPositions({
      labels: axes, cx, cy, radius, N,
      fontSize: tickFont.fontSize,
      fontFamily: tickFont.fontFamily,
    });
  }, [config.showAxisLabels, axes, cx, cy, radius, N, tickFont.fontSize, tickFont.fontFamily]);

  // ─── Tooltip ─────────────────────────────────────────────────────────────────
  const { containerRef, showTip, hideTooltip, Tip } = useChartTooltip(theme);

  if (!N) return null;

  // Legend vertical position: below all axis labels (or below the polygon)
  const legendBaseY = (() => {
    if (resolvedLabels.positions.length > 0) {
      const maxBottom = Math.max(...resolvedLabels.positions.map(p => {
        const bb = labelBBox(p.x, p.y, p.width, p.height, p.textAnchor, p.dominantBaseline);
        return bb.bottom;
      }));
      return maxBottom + 12;
    }
    return cy + radius + 20;
  })();

  return (
    <div ref={containerRef} style={{ width, height, position: 'relative', backgroundColor: theme.background }}>
      {/* Title */}
      <ChartTitle title={config.title} chartWidth={width} marginLeft={0} theme={theme} centered />

      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" style={{ width: "100%", height: "100%" }} onMouseLeave={hideTooltip}>
        {/* Grid: concentric spider polygons */}
        {gridLevels.map(level => {
          const points = axes.map((_, i) => {
            const pt = polar(cx, cy, radius, angleFor(i), level);
            return `${pt.x},${pt.y}`;
          }).join(' ');
          return (
            <polygon
              key={`grid-${level}`}
              points={points}
              fill="none"
              stroke={gridColor}
              strokeWidth={0.8}
              opacity={gridAlpha}
            />
          );
        })}

        {/* Axis spokes from center to each vertex */}
        {axes.map((_, i) => {
          const tip = polar(cx, cy, radius, angleFor(i), 1);
          return (
            <line
              key={`spoke-${i}`}
              x1={cx}
              y1={cy}
              x2={tip.x}
              y2={tip.y}
              stroke={gridColor}
              strokeWidth={0.8}
              opacity={gridAlpha}
            />
          );
        })}

        {/* Series polygons — render darkest first so lightest is on top */}
        {[...data].reverse().map((series, ri) => {
          const si = data.length - 1 - ri;
          const color = colors[si] || '#4C6EE6';
          const polygonPoints = axes.map((axis, i) => {
            const t = (series.values[axis] || 0) / maxPerAxis[axis];
            const pt = polar(cx, cy, radius, angleFor(i), t);
            return `${pt.x},${pt.y}`;
          }).join(' ');

          return (
            <g key={`series-${si}`}>
              <polygon
                points={polygonPoints}
                fill={color}
                fillOpacity={fillAlpha}
                stroke={color}
                strokeWidth={2}
                strokeLinejoin="round"
              />
              {/* Vertex dots */}
              {axes.map((axis, i) => {
                const t = (series.values[axis] || 0) / maxPerAxis[axis];
                const pt = polar(cx, cy, radius, angleFor(i), t);
                return (
                  <circle
                    key={`dot-${si}-${i}`}
                    cx={pt.x}
                    cy={pt.y}
                    r={3.5}
                    fill={color}
                    style={{ cursor: 'pointer' }}
                    onMouseMove={(e) => showTip(e, String(series.values[axis]))}
                  />
                );
              })}
              {/* Value labels at vertices */}
              {config.showValues && axes.map((axis, i) => {
                const angle = angleFor(i);
                const t = (series.values[axis] || 0) / maxPerAxis[axis];
                const pt = polar(cx, cy, radius, angle, t);
                const labelOffset = 14;
                const lx = pt.x + labelOffset * Math.cos(angle);
                const ly = pt.y + labelOffset * Math.sin(angle);
                const { textAnchor } = labelAlignment(angle);
                return (
                  <text
                    key={`val-${si}-${i}`}
                    x={lx}
                    y={ly}
                    textAnchor={textAnchor}
                    dominantBaseline="middle"
                    fontSize={theme.valueLabelSize ?? 12}
                    fill={color}
                    fontFamily={tickFont.fontFamily}
                  >
                    {series.values[axis]}
                  </text>
                );
              })}
            </g>
          );
        })}

        {/* Axis labels — collision-resolved positions */}
        {config.showAxisLabels && resolvedLabels.positions.map((pos, i) => (
          <text
            key={`label-${i}`}
            x={pos.x}
            y={pos.y}
            textAnchor={pos.textAnchor}
            dominantBaseline={pos.dominantBaseline}
            {...tickFont}
            fontSize={resolvedLabels.fontSize}
          >
            {pos.displayLabel}
          </text>
        ))}

        {/* Legend — wrapping centered rows */}
        {config.showLegend && data.length > 1 && legendLayout.rows.map((row, rowIdx) => {
          const rs = LEGEND_SPACING.rectSize;
          const rowStartX = (width - row.width) / 2;
          const rowY = legendBaseY + rowIdx * legendLayout.rowHeight;
          return (
            <g key={`legend-row-${rowIdx}`}>
              {row.items.map((item, k) => {
                const color = colors[item.idx] || '#4C6EE6';
                let lx = rowStartX;
                for (let m = 0; m < k; m++) lx += row.items[m].totalWidth + LEGEND_SPACING.itemGap;
                return (
                  <g key={`legend-${item.idx}`} transform={`translate(${lx}, ${rowY})`}>
                    <rect x={0} y={-Math.round(rs / 2)} width={rs} height={rs} fill={color} fillOpacity={0.8} />
                    <text
                      x={rs + 4}
                      y={1}
                      dominantBaseline="middle"
                      {...legendFont}
                    >
                      {item.name}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <Tip />
    </div>
  );
}

export default RadarPreview;
